import type { Rarity } from '@/game/chests';

/**
 * The chest screen's one clock. Everything the screen shows — the chest's
 * pose, the ground's colour, the title, the cards, the buttons — is a pure
 * read of this timeline at its time `t`, and `t` only moves through
 * `step(dt)`, so a capture that steps it frame by frame films exactly what a
 * player sees. The timeline only animates a roll the server already made:
 * nothing here decides anything.
 *
 *   land      the chest drops in at its starting rarity (taps count from LAND_TAP)
 *   idle      waiting for a tap ("TAP TO OPEN")
 *   charge    the chest gathers (CHARGE_S), then either
 *   upgrade   … pops to the next rarity (the ground recolours), then the next
 *             charge on its own — ONE tap plays the whole climb (a tap during
 *             it skips to the open, `skip`)
 *   open      … or, with no upgrade left, the lid flies open
 *   reveal    the cards come out one by one, then COLLECT
 *   collect   COLLECT pressed: the rewards fly to the top bar
 */

export type ChestPhase = 'land' | 'idle' | 'charge' | 'upgrade' | 'open' | 'reveal' | 'collect';

export type ChestEvent =
  | { type: 'land' }
  | { type: 'charge'; opening: boolean }
  | { type: 'upgrade'; to: Rarity; index: number }
  | { type: 'open'; rarity: Rarity }
  | { type: 'card'; index: number }
  | { type: 'ready' }
  | { type: 'collect' };

/** Seconds. */
export const LAND_FALL_S = 0.42;
export const LAND_S = 0.95;
export const LAND_TAP_S = 0.55;
export const CHARGE_S = 0.34;
export const CHARGE_OPEN_S = 0.42;
export const UPGRADE_S = 0.62;
/** Recolour of the ground and the chest after a pop. */
export const RECOLOUR_S = 0.4;
/**
 * The reveal's card beats, Blockari's own: a card leaves the chest's mouth on an arc and lands in its slot in the row
 * (no beat at centre-stage, no flip), the next leaves CARD_GAP_S after it, and COLLECT follows the last landing.
 */
export const FIRST_CARD_S = 0.62;
export const CARD_GAP_S = 0.36;
export const CARD_FLIGHT_S = 0.46;
/** From the last card landing to COLLECT. */
export const READY_S = 0.3;
export const COLLECT_S = 0.62;

/** A tap during the reveal plays the rest at this many times the speed (the beats stay in order, just quicker). */
export const SKIP_RATE = 8;

/**
 * Reduced motion: the cards simply fade in, one after another, in their places. These are the whole reveal's
 * timings (the chest's own lid still opens on the charge): nothing flies or turns.
 */
export const REDUCED = { firstCard: 0.12, gap: 0.08, flight: 0.2, ready: 0.05 } as const;

export class ChestTimeline {
  t = 0;
  phase: ChestPhase = 'land';
  /** Time the current phase began. */
  phaseAt = 0;
  /** Upgrades popped so far. */
  popped = 0;
  /** Time of each pop (the recolour and the title swap run from it). */
  readonly popAt: number[] = [];
  /** Time the lid flew (null until then). */
  openAt: number | null = null;
  collectAt: number | null = null;
  private readyFired = false;
  private cardsFired = 0;
  private readonly queue: ChestEvent[] = [];

  /**
   * When the chest stood on the ground, ready for a tap: LAND_FALL_S into its
   * own land, or — handed over from the arrival (`landed`) — the moment the
   * reveal began, on the arrival's clock (`at`): the chest is already there.
   */
  readonly landedAt: number;
  /** When this reveal's clock began (0 on its own; the arrival's time on a hand-over). */
  readonly origin: number;
  /** Whether this reveal took its chest over from the arrival (no land of its own). */
  readonly handedOver: boolean;
  /** Reduced motion: the cards fade in, nothing else moves. */
  readonly reduced: boolean;
  /** A tap in the reveal is playing the rest quickly. */
  skipping = false;
  /** The card timings in force (seconds): Blockari's, or the reduced fade's. */
  readonly firstCard: number;
  readonly gap: number;
  readonly flight: number;
  readonly readyHold: number;

  constructor(
    readonly start: Rarity,
    readonly steps: readonly Rarity[],
    readonly cardCount: number,
    opts: { at?: number; landed?: boolean; reduced?: boolean } = {},
  ) {
    const at = opts.at ?? 0;
    this.origin = at;
    this.handedOver = opts.landed === true;
    this.reduced = opts.reduced === true;
    this.firstCard = this.reduced ? REDUCED.firstCard : FIRST_CARD_S;
    this.gap = this.reduced ? REDUCED.gap : CARD_GAP_S;
    this.flight = this.reduced ? REDUCED.flight : CARD_FLIGHT_S;
    this.readyHold = this.reduced ? REDUCED.ready : READY_S;
    this.t = at;
    this.phaseAt = at;
    if (opts.landed) {
      this.phase = 'idle';
      this.landedAt = at;
    } else {
      this.landedAt = at + LAND_FALL_S;
      this.queue.push({ type: 'land' });
    }
  }

  /** The rarity on screen: the start, then each popped step. */
  get rarity(): Rarity {
    return this.popped === 0 ? this.start : (this.steps[this.popped - 1] ?? this.start);
  }

  /** The rarity before the latest pop (the recolour's "from"). */
  get previous(): Rarity {
    return this.popped <= 1 ? this.start : (this.steps[this.popped - 2] ?? this.start);
  }

  /** 0..1 through the latest recolour (1 when none is running). */
  get recolour(): number {
    const at = this.popAt[this.popped - 1];
    return at === undefined ? 1 : Math.min(1, Math.max(0, (this.t - at) / RECOLOUR_S));
  }

  /** Seconds since the current phase began. */
  get local(): number {
    return this.t - this.phaseAt;
  }

  /**
   * The climb plays itself: once the first tap has started it, each rung's
   * charge follows the last upgrade (UPGRADE_S) on this clock, then the lid
   * flies. A tap during the climb skips straight to the open (`skip`).
   */
  climbing = false;

  /** Whether a tap now would do something (start the climb, or skip the rest of it). */
  get tappable(): boolean {
    if (this.openAt !== null) return false;
    if (this.phase === 'land') return this.local >= LAND_TAP_S;
    if (this.phase === 'idle') return true;
    return this.climbing && (this.phase === 'charge' || this.phase === 'upgrade');
  }

  get opening(): boolean {
    return this.popped >= this.steps.length;
  }

  /**
   * A tap on the chest: starts the climb, or during it skips to the open; during the reveal it plays the rest
   * of the cards quickly, straight to COLLECT (never past it: that is its own button). Returns whether it was taken.
   */
  tap(): boolean {
    if (this.phase === 'reveal' && !this.readyFired && this.collectAt === null) {
      if (this.skipping) return false;
      this.skipping = true;
      return true;
    }
    if (!this.tappable) return false;
    if (this.climbing) return this.skip();
    this.climbing = true;
    this.go('charge');
    this.queue.push({ type: 'charge', opening: this.opening });
    return true;
  }

  /**
   * Skip the rest of the climb: every remaining stored step is popped on this
   * frame (the ladder lights every rung, the title lands on the final rarity,
   * one upgrade cue for the last), and the lid flies now — from here on it is
   * the same frame-for-frame as playing through, measured from the open.
   */
  skip(): boolean {
    if (this.openAt !== null) return false;
    const t = this.t;
    let last = -1;
    while (this.popped < this.steps.length) {
      this.popped++;
      this.popAt.push(t);
      last = this.popped - 1;
    }
    if (last >= 0) this.queue.push({ type: 'upgrade', to: this.rarity, index: last });
    this.openAt = t;
    this.go('open', t);
    this.queue.push({ type: 'open', rarity: this.rarity });
    return true;
  }

  /** COLLECT. Returns whether it was taken. */
  collect(): boolean {
    if (this.phase !== 'reveal' || !this.readyFired) return false;
    this.collectAt = this.t;
    this.go('collect');
    this.queue.push({ type: 'collect' });
    return true;
  }

  /** When card i leaves the chest (seconds), or null before the lid has flown. */
  cardStart(i: number): number | null {
    return this.openAt === null ? null : this.openAt + this.firstCard + i * this.gap;
  }

  /** 0..1 through card i's flight out of the chest. */
  cardProgress(i: number): number {
    const s = this.cardStart(i);
    if (s === null) return 0;
    return Math.min(1, Math.max(0, (this.t - s) / this.flight));
  }

  /** When COLLECT appears. */
  get readyAt(): number | null {
    const last = this.cardStart(Math.max(0, this.cardCount - 1));
    return last === null ? null : last + this.flight + this.readyHold;
  }

  step(dt: number): void {
    const rate = this.skipping && !this.readyFired ? SKIP_RATE : 1;
    this.t += Math.max(0, dt) * rate;
    for (let guard = 0; guard < 8; guard++) if (!this.advance()) break;
  }

  /** Events since the last drain, in order. */
  drain(): ChestEvent[] {
    return this.queue.splice(0, this.queue.length);
  }

  private go(p: ChestPhase, at = this.t): void {
    this.phase = p;
    this.phaseAt = at;
  }

  private advance(): boolean {
    switch (this.phase) {
      case 'land':
        if (this.local >= LAND_S) {
          this.go('idle', this.phaseAt + LAND_S);
          return true;
        }
        return false;
      case 'charge': {
        const opening = this.opening;
        const dur = opening ? CHARGE_OPEN_S : CHARGE_S;
        if (this.local < dur) return false;
        const at = this.phaseAt + dur;
        if (opening) {
          this.openAt = at;
          this.go('open', at);
          this.queue.push({ type: 'open', rarity: this.rarity });
        } else {
          this.popped++;
          this.popAt.push(at);
          this.go('upgrade', at);
          this.queue.push({ type: 'upgrade', to: this.rarity, index: this.popped - 1 });
        }
        return true;
      }
      case 'upgrade':
        if (this.local >= UPGRADE_S) {
          const at = this.phaseAt + UPGRADE_S;
          // The climb plays on: the next rung's charge (or the open's) starts as this one ends.
          if (this.climbing) {
            this.go('charge', at);
            this.queue.push({ type: 'charge', opening: this.opening });
          } else this.go('idle', at);
          return true;
        }
        return false;
      case 'open':
        if (this.local >= this.firstCard) {
          this.go('reveal', this.phaseAt + this.firstCard);
          return true;
        }
        return false;
      case 'reveal': {
        let moved = false;
        while (this.cardsFired < this.cardCount) {
          const s = this.cardStart(this.cardsFired);
          if (s === null || this.t < s) break;
          this.queue.push({ type: 'card', index: this.cardsFired });
          this.cardsFired++;
          moved = true;
        }
        const ready = this.readyAt;
        if (!this.readyFired && ready !== null && this.t >= ready) {
          this.readyFired = true;
          this.queue.push({ type: 'ready' });
          moved = true;
        }
        return moved;
      }
      default:
        return false;
    }
  }

  /** COLLECT is showing and takes a press. */
  get ready(): boolean {
    return this.readyFired && this.phase === 'reveal';
  }
}

/* --------------------------------------------------------------------------
   Easing
   -------------------------------------------------------------------------- */

export const clamp01 = (u: number): number => Math.min(1, Math.max(0, u));
export const easeOutCubic = (u: number): number => 1 - Math.pow(1 - clamp01(u), 3);
export const easeInCubic = (u: number): number => Math.pow(clamp01(u), 3);
export const easeOutBack = (u: number, k = 1.9): number => {
  const x = clamp01(u) - 1;
  return 1 + (k + 1) * x * x * x + k * x * x;
};
/** A damped spring from 1 toward 0 (starts at 1, rings, settles). */
export const spring = (t: number, freq = 9, damp = 7): number =>
  t < 0 ? 0 : Math.exp(-damp * t) * Math.cos(freq * t);
